import { useEffect, useRef, useState } from 'react'
import { Icone, type NomDIcone } from '@/components/ui/Icone'
import { MOTIFS, type MotifDeSignalement, type StatsDeLaQuestion } from '@/lib/api/feed'

/**
 * La barre d'actions d'une carte — 06 §6.3, RG-D12, RG-D12bis.
 *
 * **La grammaire d'un post.** Les gestes fréquents sont en bas, sous le pouce,
 * en icônes, et chacun porte son chiffre à côté : marquer, expliquer, aller à
 * la leçon, envoyer. Ce qui est rare ou grave vit dans le menu **trois points**,
 * en haut de la carte.
 *
 * **Un seul symbole par action.** Le compte de marque-pages se lit À CÔTÉ du
 * marque-page, et l'on marque en tapant dessus — comme un post. Un second
 * symbole ailleurs, en lecture seule, donnait deux fois la même information et
 * une seule des deux était cliquable.
 *
 * **L'ampoule est toujours là, éteinte tant qu'on n'a pas répondu.** La faire
 * apparaître après la réponse déplaçait toute la barre sous le doigt, au
 * moment précis où le candidat s'apprête à taper la carte suivante.
 */
export function ActionBar({
  marque,
  peutExpliquer,
  explicationOuverte = false,
  stats,
  onMarquer,
  onExpliquer,
  onLecon,
  onPartager,
}: {
  marque: boolean
  /** L'explication n'existe qu'après avoir répondu (RG-2.4ter). */
  peutExpliquer: boolean
  explicationOuverte?: boolean | undefined
  stats?: StatsDeLaQuestion | undefined
  onMarquer: (marque: boolean) => void
  onExpliquer?: (() => void) | undefined
  onLecon?: (() => void) | undefined
  onPartager?: (() => void) | undefined
}) {
  return (
    // **Une GRILLE à colonnes égales, et non un `justify-around`** — §103.
    //
    // `justify-around` répartit également les BOÎTES, pas les icônes. Or chaque
    // action porte désormais son compteur : « 0 », « 12 », « 1,2 k » n'ont pas
    // la même largeur, la boîte s'élargit d'autant, et l'icône se décale dans
    // sa boîte. À gabarit d'icônes identique, les écarts restaient inégaux —
    // ce que l'œil voit immédiatement sur une rangée.
    //
    // `grid-flow-col` + `auto-cols-fr` donne à chaque cellule EXACTEMENT la
    // même largeur, quel que soit son contenu ; centrée dans sa cellule, chaque
    // icône tombe alors à intervalle constant. Les statistiques entrent dans le
    // même rythme : elles appartiennent à la même rangée, et les en sortir
    // rompait précisément l'alignement qu'on cherche.
    <div
      className="mt-3 grid grid-flow-col auto-cols-fr items-center"
      style={{ color: 'var(--texte-2)' }}
    >
      <>
        <Action
          // **Plein quand c'est marqué**, à la couleur du thème, comme le
          // marque-page de Twitter : la forme dit l'état sans qu'on lise.
          nom={marque ? 'marque-page-plein' : 'marque-page'}
          libelle={marque ? 'Retirer des marque-pages' : 'Marquer'}
          actif={marque}
          compte={stats?.marquee}
          onClick={() => { onMarquer(!marque); }}
        />

        {onExpliquer !== undefined && (
          <Action
            nom="explication"
            libelle={peutExpliquer ? 'Voir l’explication' : 'Répondez d’abord'}
            actif={explicationOuverte}
            // Éteinte avant la réponse : présente, à sa place, et inerte. Le
            // candidat sait qu'elle existe et qu'elle attend son tour.
            eteinte={!peutExpliquer}
            onClick={() => { if (peutExpliquer) onExpliquer(); }}
          />
        )}

        {onLecon !== undefined && (
          <Action nom="lecon" libelle="Aller à la leçon" onClick={onLecon} />
        )}

        {onPartager !== undefined && (
          <Action
            nom="partager"
            libelle="Envoyer à un ami"
            compte={stats?.partagee}
            onClick={onPartager}
          />
        )}
      </>

      {/* RG-2.42 — ce que la question a produit, au MÊME format que le reste
          de la barre : une icône, un chiffre. Une phrase au milieu d'icônes
          rompait l'alignement et se lisait comme une note de bas de page.
          Chacune occupe sa propre cellule de la grille, comme les actions :
          c'est ce qui donne à toute la rangée un pas régulier. */}
      {stats !== undefined && stats.repondue > 0 && (
        <Chiffre nom="reponses" valeur={compact(stats.repondue)} libelle="réponses" />
      )}

      {stats !== undefined && stats.repondue > 0 && stats.reussite !== null && (
        <Chiffre
          nom="cible"
          valeur={`${String(Math.round(stats.reussite * 100))} %`}
          libelle="de réussite"
        />
      )}
    </div>
  )
}

/**
 * Les grands nombres, à la façon d'un réseau social — 1,2 k plutôt que 1 247.
 *
 * **Le chiffre exact n'apprend rien ici.** Ce que le candidat lit, c'est un
 * ordre de grandeur : « beaucoup de monde a répondu » ou « presque personne ».
 * Et 1 247 sur quatre lignes d'icônes fait déborder la barre sur un téléphone.
 *
 * La virgule est la séparatrice décimale française : « 1,2 k », pas « 1.2k ».
 */
export function compact(n: number): string {
  if (n < 1000) return String(n)

  if (n < 1_000_000) {
    const milliers = n / 1000

    return `${milliers < 10 ? milliers.toFixed(1).replace('.', ',') : String(Math.round(milliers))} k`
  }

  const millions = n / 1_000_000

  return `${millions < 10 ? millions.toFixed(1).replace('.', ',') : String(Math.round(millions))} M`
}

/** Une statistique en lecture seule : icône, chiffre, et rien de cliquable. */
function Chiffre({
  nom,
  valeur,
  libelle,
}: {
  nom: NomDIcone
  valeur: string
  libelle: string
}) {
  return (
    // **Même gabarit qu'une action** : 19 px d'icône, même hauteur de ligne,
    // même écart. À 16 px, les deux chiffres de droite paraissaient d'une
    // autre famille que les icônes de gauche, et la barre se lisait en deux
    // morceaux. Ce ne sont pas des boutons — d'où l'absence de cible tactile
    // et de curseur — mais ils appartiennent à la même rangée.
    <span
      className="-my-2 flex min-h-11 items-center justify-center gap-1 py-2 text-[13px] tabular-nums"
      title={libelle}
    >
      <Icone nom={nom} taille={19} />
      {valeur}
      <span className="sr-only">{libelle}</span>
    </span>
  )
}

function Action({
  nom,
  libelle,
  actif = false,
  eteinte = false,
  compte,
  onClick,
}: {
  nom: NomDIcone
  libelle: string
  actif?: boolean
  eteinte?: boolean
  compte?: number | undefined
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={libelle}
      aria-pressed={actif}
      aria-disabled={eteinte}
      title={libelle}
      // 44 px de cible : le Fil se joue au pouce, dans un tap-tap.
      className="-my-2 flex min-h-11 items-center justify-center gap-1 rounded-full px-2 py-2 transition-colors"
      style={{
        color: actif ? 'var(--accent-fort)' : undefined,
        // Éteinte : plus pâle que le texte secondaire, sans disparaître.
        opacity: eteinte ? 0.4 : 1,
        cursor: eteinte ? 'default' : 'pointer',
      }}
    >
      <Icone nom={nom} taille={19} />

      {/* **Zéro s'affiche** — RG-2.42 précisée, §97. Le masquer faisait sauter
          les icônes d'une carte à l'autre : la barre changeait de largeur selon
          la popularité de la question, et l'œil devait la relire à chaque
          défilement. Un zéro est par ailleurs une information — « personne ne
          l'a mise de côté » se lit, une absence ne se lit pas. */}
      {compte !== undefined && (
        <span className="text-[13px] tabular-nums">{compact(compte)}</span>
      )}
    </button>
  )
}

/**
 * Le menu des trois points — ce qui est rare ou grave.
 *
 * Signaler vit ici, et nulle part ailleurs. Un motif CHOISI se compte, se
 * recoupe entre candidats et déclenche une dépublication (RG-04-55) ; « il y a
 * un problème » ne fait rien de tout cela.
 *
 * **Le partage hors du site y vit aussi** — RG-2.1bis, [DECISIONS §192]. La
 * grammaire posée le 7 août range « copier le lien » ici, et c'est la bonne
 * place pour une raison qui s'est confirmée depuis : le candidat n'a que TROIS
 * partages extérieurs par jour. Une sixième icône sous le pouce, entre marquer
 * et envoyer à un ami, aurait invité au geste d'humeur — et fait dépenser en
 * défilant une réserve qui se compte.
 */
export function MenuDeLaCarte({
  signale,
  stats,
  onSignaler,
  onPartagerDehors,
}: {
  signale: boolean
  stats?: StatsDeLaQuestion | undefined
  onSignaler: (motif: MotifDeSignalement) => void
  /** RG-2.47 — l'adresse publique de cette question. Absent : rien à proposer. */
  onPartagerDehors?: (() => void) | undefined
}) {
  const [ouvert, setOuvert] = useState(false)
  const boite = useRef<HTMLDivElement>(null)

  // Un menu qui ne se ferme qu'en rouvrant reste ouvert derrière la carte
  // suivante quand on défile.
  useEffect(() => {
    if (!ouvert) return

    const dehors = (e: MouseEvent): void => {
      if (boite.current !== null && !boite.current.contains(e.target as Node)) {
        setOuvert(false)
      }
    }

    const echap = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOuvert(false)
    }

    document.addEventListener('mousedown', dehors)
    document.addEventListener('keydown', echap)

    return () => {
      document.removeEventListener('mousedown', dehors)
      document.removeEventListener('keydown', echap)
    }
  }, [ouvert])

  return (
    <div ref={boite} className="relative">
      <button
        type="button"
        onClick={() => { setOuvert((avant) => !avant); }}
        aria-expanded={ouvert}
        aria-label="Plus d’actions"
        className="-m-2 flex min-h-11 min-w-11 items-center justify-center rounded-full p-2"
        style={{ color: 'var(--texte-2)' }}
      >
        {/* Même gabarit que les actions : à 17 px, le menu paraissait d'une
            autre famille que la rangée qu'il termine. */}
        <Icone nom="trois-points" taille={19} />
      </button>

      {ouvert && (
        <div
          role="menu"
          className="absolute right-0 z-10 mt-1 flex w-72 flex-col rounded-2xl border py-1 shadow-lg"
          style={{ borderColor: 'var(--bordure)', background: 'var(--fond)' }}
        >
          {/* Les VUES vivent ici — RG-2.42. Elles intéressent celui qui
              s'apprête à signaler (« combien l'ont vue avant moi ? »), pas
              celui qui révise. Les marque-pages et les partages, eux, sont
              déjà sous leur propre icône. */}
          {stats !== undefined && (
            <p
              className="flex items-center gap-2 border-b px-4 pb-2 pt-2 text-[13px]"
              style={{ color: 'var(--texte-2)', borderColor: 'var(--bordure)' }}
            >
              <Icone nom="oeil" taille={16} />
              {compact(stats.vue)} vue{stats.vue > 1 ? 's' : ''}
            </p>
          )}

          {/* RG-2.47, §192 — l'adresse publique de la question. En PREMIER,
              au-dessus du signalement : c'est le seul des deux qu'on fait pour
              une bonne raison, et le poser sous sept motifs de plainte
              l'aurait rendu introuvable. */}
          {onPartagerDehors !== undefined && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOuvert(false)
                onPartagerDehors()
              }}
              className="flex min-h-11 items-center gap-3 border-b px-4 text-left text-[15px] hover:bg-[var(--surface-2)]"
              style={{ borderColor: 'var(--bordure)' }}
            >
              <Icone nom="lien" taille={16} />
              Partager hors de Haicademy
            </button>
          )}

          {signale ? (
            // RG-04-54 — le candidat doit voir que son geste a compté.
            <p className="px-4 py-3 text-[13px]" style={{ color: 'var(--texte-2)' }}>
              Signalé. Cette question ne vous sera plus servie ; un éditeur la
              relira.
            </p>
          ) : (
            <>
              <p
                className="px-4 pb-1 pt-2 text-[13px] font-bold"
                style={{ color: 'var(--texte-2)' }}
              >
                Signaler — qu’est-ce qui ne va pas ?
              </p>

              {MOTIFS.map((motif) => (
                <button
                  key={motif.value}
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setOuvert(false)
                    onSignaler(motif.value)
                  }}
                  className="flex min-h-11 items-center gap-3 px-4 text-left text-[15px] hover:bg-[var(--surface-2)]"
                >
                  <Icone nom="drapeau" taille={16} />
                  {motif.label}
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  )
}
